import { type FormEvent, useEffect, useState } from 'react';
import { AuthCard, authButtonClass, authInputClass } from './AuthCard';

type Status = 'loading' | 'valid' | 'invalid';

/** Public invite redemption: validate the token, collect name + password, create
 *  the account (auto-signed-in server-side), then land in the app. */
export const AcceptInvitePage = () => {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [status, setStatus] = useState<Status>('loading');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    fetch(`/api/invitations/validate?token=${encodeURIComponent(token)}`)
      .then(async (response) => {
        if (!response.ok) return setStatus('invalid');
        const body = (await response.json()) as { email: string };
        setEmail(body.email);
        setStatus('valid');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const response = await fetch('/api/invitations/accept', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, name, password }),
    });
    if (response.ok) {
      window.location.href = '/';
      return;
    }
    setSubmitting(false);
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    setError(
      body.error === 'ACCOUNT_EXISTS'
        ? 'An account already exists for this email.'
        : body.error === 'INVALID_INVITATION'
          ? 'This invitation is invalid or has expired.'
          : 'Something went wrong. Please try again.',
    );
  };

  if (status === 'loading') {
    return (
      <AuthCard>
        <p className="text-center text-sm text-content-subtle">Checking your invitation…</p>
      </AuthCard>
    );
  }

  if (status === 'invalid') {
    return (
      <AuthCard>
        <p className="text-center text-sm text-content-deemphasized">
          This invitation link is invalid or has expired. Ask whoever invited you to send a new one.
        </p>
        <div className="mt-6 text-center text-xs text-content-subtle">
          <a href="/" className="hover:text-secondary-emphasized">
            Back to sign in
          </a>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <p className="mb-4 text-center text-sm text-content-deemphasized">
        Setting up your account for <span className="font-medium text-content-text">{email}</span>
      </p>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm font-medium text-content-text">
          Your name
          <input
            type="text"
            autoComplete="name"
            required={true}
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={authInputClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium text-content-text">
          Choose a password
          <input
            type="password"
            autoComplete="new-password"
            required={true}
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className={authInputClass}
          />
        </label>
        {error ? <p className="text-sm text-error-main">{error}</p> : null}
        <button type="submit" disabled={submitting} className={authButtonClass}>
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </AuthCard>
  );
};
